/** Reads the spread (A5) current-estimate read model (GET /spread?cells=) and adapts
 *  it into synthetic app.aid.spread.estimate events so the existing SpreadStore folds
 *  the viewport seed exactly like live estimates. Read-only.
 *
 *  Note: unlike IncidentsClient (corroboration hosts many routes under /corroboration),
 *  the spread service's collection endpoint IS the base path, so spreadInCells appends
 *  only the query string (`${baseUrl}?cells=…`), not a route segment. */

/** One estimate from GET /spread — the flat shape the Rust read API serves. */
export interface ReadModelSpread {
  subject: string;
  status: string;
  confidence: number;
  horizonMinutes: number;
  pathCells: string[];
  bearingDeg?: number;
  speedKmh?: number;
}

export interface SpreadClientOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

export class SpreadClient {
  constructor(private readonly opts: SpreadClientOptions) {}
  private get fetch(): typeof fetch {
    return this.opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** Current estimates whose path cells intersect `cells` (O(active), no history). */
  async spreadInCells(cells: string[]): Promise<ReadModelSpread[]> {
    if (cells.length === 0) return [];
    const res = await this.fetch(`${this.opts.baseUrl}?cells=${encodeURIComponent(cells.join(","))}`);
    if (!res.ok) throw new Error(`spread fetch failed: ${res.status} ${res.statusText}`);
    return (await res.json()) as ReadModelSpread[];
  }
}

/** Convert read-model estimates to synthetic app.aid.spread.estimate events. seq 0 ⇒
 *  any live-tailed estimate for the same subject wins (SpreadStore latest-wins by seq). Pure. */
export function seedSpreadEventsFromReadModel(
  views: ReadModelSpread[],
): Array<{ seq: number; json: Record<string, unknown> }> {
  return views.map((v) => ({
    seq: 0,
    json: {
      $type: "app.aid.spread.estimate",
      payload: {
        subject: v.subject,
        status: v.status,
        confidence: v.confidence,
        horizonMinutes: v.horizonMinutes,
        pathCells: v.pathCells,
        bearingDeg: v.bearingDeg,
        speedKmh: v.speedKmh,
      },
    },
  }));
}
